'use client'

import { useAuthorization } from '@entities/authentication'
import { subscribesEntityQueries } from '@entities/subscribes'
import { userEntityQueries, type WelcomeBackPromotionResponse } from '@entities/users'
import { requestOnboardingState } from '@features/trial-booking/api'
import { canShowFirstTrialPopup } from '@features/trial-booking/eligibility'
import { FEATURE_FLAG_KEYS } from '@shared/constants'
import { Carousel, type CarouselApi, CarouselContent, CarouselIndicator, CarouselItem } from '@shared/ui'
import { normalizeCountryCode } from '@shared/utils'
import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import Autoplay from 'embla-carousel-autoplay'
import Image from 'next/image'
import { useRouter } from 'next/navigation'
import { ComponentProps, ReactElement, useCallback, useEffect, useMemo, useState } from 'react'

import { useFlagsCompat } from '@podo/flags/react'

import { shouldShowHexagonGmiBanner } from '../model/hexagon-gmi-eligibility'
import {
  getBookedLanguageTrialTypes,
  getPurchaseableTrialLanguageTypes,
  getResumableAdditionalTrialLanguageTypes,
  shouldShowLanguageTrialBanner,
  type LanguageTrialType,
} from '../model/language-trial-eligibility'
import { AiLearningBanner } from './ai-learning-banner'
import { AiLearningCoffeeEventBanner } from './ai-learning-coffee-event-banner'
import { AiLearningCoffeeMachineEventBanner } from './ai-learning-coffee-machine-event-banner'
import { AiLearningOneWeekBanner } from './ai-learning-one-week-banner'
import { DefaultBanner } from './default-banner'
import { EventBanner } from './event-banner'
import { HexagonGmiBanner } from './hexagon-gmi-banner'
import { LanguageTrialBanner } from './language-trial-banner'
import { TrialBanner } from './trial-banner'
import { WelcomeBackBanner } from './welcomeback-banner'

// ============================================================
// Constants
// ============================================================

const EVENT_BANNERS = [
  {
    code: '20250203',
    startDate: new Date('2025-02-01'),
    endDate: new Date('2025-03-01'),
    url: 'https://podospeaking.com/togetherpodo?external=true',
  },
  {
    code: '20250401',
    startDate: null, // Always active
    endDate: null,
    url: 'https://podospeaking.com/togetherpodo?external=true',
  },
] as const

const PODOLINGO_EVENT_BANNER_URL = 'https://formbricks.hrsong99.xyz/s/snctj93f5cuttd05m3x9t71p?external=true'
const HEXAGON_GMI_BANNER_URL = 'https://www.hexagongmi.com/?external=true'

export const HOME_BANNER_IMAGE_PROPS: Partial<ComponentProps<typeof Image>> = {
  width: 1005,
  height: 252,
  className: 'h-full w-full object-cover',
  sizes: '100vw',
}

export const HOME_BANNER_CONTAINER_CLASS_NAME = 'ml-0 aspect-[335/84] overflow-hidden rounded-lg'

// ============================================================
// Types
// ============================================================

type BannerItem = {
  id: string
  component: ReactElement
}

type CarouselState = {
  current: number
  count: number
}

// ============================================================
// Helper Functions
// ============================================================

/**
 * 이벤트 배너가 현재 활성화되어 있는지 확인
 */
const isEventBannerActive = (event: (typeof EVENT_BANNERS)[number]): boolean => {
  // startDate가 null이면 항상 활성화
  if (!event.startDate) {
    return true
  }

  const now = new Date()
  return now >= event.startDate && (!event.endDate || now < event.endDate)
}

export const getBannerIndicatorState = ({
  bannerCount,
  carouselState,
}: {
  bannerCount: number
  carouselState: CarouselState
}): CarouselState => {
  if (bannerCount <= 0) {
    return { count: 0, current: 0 }
  }

  return {
    count: bannerCount,
    current: Math.min(Math.max(carouselState.current || 1, 1), bannerCount),
  }
}

/**
 * 배너 수집 함수의 파라미터 타입
 */
type CollectBannersParams = {
  welcomeBackPromotion: WelcomeBackPromotionResponse | null
  podolingoBannerFlag: { enabled: boolean; value?: string | number | boolean | null }
  isAdditionalTrialBannerEnabled: boolean
  isAiLearningAvailable: boolean
  canShowFirstTrialPopup: boolean
  trialClassCompYn: string
  paymentYn: string
  paymentInfo: string | null
  trialPaymentInfo: string | null
  trialClassCompInfo: string | null
  purchaseableTrialLanguages: readonly LanguageTrialType[]
  bookedOnboardingLanguages: readonly LanguageTrialType[]
  resumableOnboardingLanguages: readonly LanguageTrialType[]
  handlers: {
    handleBannerClick: () => void
    handleEventBannerClick: () => void
    handleEventClick: (url: string) => () => void
    handleHexagonGmiClick: () => void
    handleWelcomeBackClick: (url: string) => () => void
  }
}

/**
 * 조건에 맞는 모든 배너를 수집
 */
export const collectBanners = (params: CollectBannersParams): BannerItem[] => {
  const {
    welcomeBackPromotion,
    podolingoBannerFlag,
    isAdditionalTrialBannerEnabled,
    isAiLearningAvailable,
    canShowFirstTrialPopup,
    trialClassCompYn,
    paymentYn,
    paymentInfo,
    trialPaymentInfo,
    trialClassCompInfo,
    purchaseableTrialLanguages,
    bookedOnboardingLanguages,
    resumableOnboardingLanguages,
    handlers,
  } = params

  const collectedBanners: BannerItem[] = []

  // 0. WelcomeBack 프로모션 배너 (highest priority - 만료 고객 대상)
  if (welcomeBackPromotion?.eligible && welcomeBackPromotion.segment && welcomeBackPromotion.redirectUrl) {
    collectedBanners.push({
      id: `welcomeback-${welcomeBackPromotion.segment.toLowerCase()}`,
      component: (
        <WelcomeBackBanner
          segment={welcomeBackPromotion.segment}
          redirectUrl={welcomeBackPromotion.redirectUrl}
          onClickWrapper={handlers.handleWelcomeBackClick(welcomeBackPromotion.redirectUrl)}
        />
      ),
    })
  }

  // 1. Podolingo 배너 체크 (highest priority)
  if (isAiLearningAvailable && podolingoBannerFlag.enabled) {
    const bannerValue = typeof podolingoBannerFlag.value === 'string' ? podolingoBannerFlag.value : null
    if (bannerValue === 'ai-learning-one-week') {
      collectedBanners.push({
        id: 'podolingo-one-week',
        component: <AiLearningOneWeekBanner onClickWrapper={handlers.handleBannerClick} {...HOME_BANNER_IMAGE_PROPS} />,
      })
    }
    if (bannerValue === 'ai-learning-coffee-event') {
      collectedBanners.push({
        id: 'podolingo-coffee-event',
        component: (
          <AiLearningCoffeeEventBanner onClickWrapper={handlers.handleEventBannerClick} {...HOME_BANNER_IMAGE_PROPS} />
        ),
      })
    }
    if (bannerValue === 'ai-learning-coffee-machine-event') {
      collectedBanners.push({
        id: 'podolingo-coffee-machine-event',
        component: (
          <AiLearningCoffeeMachineEventBanner
            onClickWrapper={handlers.handleEventBannerClick}
            {...HOME_BANNER_IMAGE_PROPS}
          />
        ),
      })
    }
    if (bannerValue === 'ai-learning-banner') {
      collectedBanners.push({
        id: 'podolingo-ai-learning',
        component: <AiLearningBanner onClickWrapper={handlers.handleBannerClick} {...HOME_BANNER_IMAGE_PROPS} />,
      })
    }
  }

  // 2. 트라이얼 유도 배너 (미결제 & 미트라이얼)
  if (canShowFirstTrialPopup) {
    collectedBanners.push({
      id: 'trial',
      component: <TrialBanner {...HOME_BANNER_IMAGE_PROPS} />,
    })
  }

  // 3. 이벤트 배너들 (모두 활성화된 것들)
  for (const event of EVENT_BANNERS) {
    if (isEventBannerActive(event)) {
      collectedBanners.push({
        id: `event-${event.code}`,
        component: (
          <EventBanner
            code={event.code}
            onClickWrapper={handlers.handleEventClick(event.url)}
            {...HOME_BANNER_IMAGE_PROPS}
          />
        ),
      })
    }
  }

  // 4. 언어별 추가 체험 배너들 — TBD_260424_ADDITIONAL_TRIAL_BANNER 플래그로 전체 노출 제어
  if (isAdditionalTrialBannerEnabled) {
    if (
      shouldShowLanguageTrialBanner({
        langType: 'EN',
        paymentInfo,
        trialPaymentInfo,
        purchaseableTrialLanguages,
        bookedOnboardingLanguages,
        resumableOnboardingLanguages,
      })
    ) {
      collectedBanners.push({
        id: 'language-trial-en',
        component: <LanguageTrialBanner langType="EN" {...HOME_BANNER_IMAGE_PROPS} />,
      })
    }
    if (
      shouldShowLanguageTrialBanner({
        langType: 'JP',
        paymentInfo,
        trialPaymentInfo,
        purchaseableTrialLanguages,
        bookedOnboardingLanguages,
        resumableOnboardingLanguages,
      })
    ) {
      collectedBanners.push({
        id: 'language-trial-jp',
        component: <LanguageTrialBanner langType="JP" {...HOME_BANNER_IMAGE_PROPS} />,
      })
    }
  }

  // 5. Hexagon GMI K-MOVE 배너 (일본어 체험 완료자 / 일본어·더블팩 기수강생 / 만료자)
  if (
    shouldShowHexagonGmiBanner({
      trialClassCompYn,
      paymentYn,
      trialClassCompInfo,
      paymentInfo,
      welcomeBackPromotionEligible: Boolean(welcomeBackPromotion?.eligible),
    })
  ) {
    collectedBanners.push({
      id: 'hexagon-gmi',
      component: <HexagonGmiBanner onClickWrapper={handlers.handleHexagonGmiClick} {...HOME_BANNER_IMAGE_PROPS} />,
    })
  }

  if (collectedBanners.length === 0) {
    collectedBanners.push({
      id: 'default',
      component: <DefaultBanner {...HOME_BANNER_IMAGE_PROPS} />,
    })
  }

  return collectedBanners
}

// ============================================================
// Component
// ============================================================

export const LegacyHomeBannerCarousel = () => {
  const { bearerToken } = useAuthorization()
  const router = useRouter()

  const [carouselApi, setCarouselApi] = useState<CarouselApi>()
  const [carouselState, setCarouselState] = useState<CarouselState>({
    current: 0,
    count: 0,
  })

  const { data: userInfo } = useSuspenseQuery({
    ...userEntityQueries.getCurrentUser({ bearerToken }),
  })

  // WelcomeBack 프로모션 데이터 (만료 고객 대상 - 실패해도 배너만 안보이면 됨)
  const { data: welcomeBackPromotion } = useQuery({
    ...userEntityQueries.getWelcomeBackPromotion({ bearerToken }),
  })

  const { trialClassCompYn, paymentYn, paymentInfo, trialPaymentInfo, trialClassCompInfo } = userInfo

  // Check feature flags
  const {
    podolingo_banner: podolingoBannerFlag,
    [FEATURE_FLAG_KEYS.TBD_260424_ADDITIONAL_TRIAL_BANNER]: additionalTrialBannerFlag,
  } = useFlagsCompat([FEATURE_FLAG_KEYS.PODOLINGO_BANNER, FEATURE_FLAG_KEYS.TBD_260424_ADDITIONAL_TRIAL_BANNER])

  const isAdditionalTrialBannerEnabled = additionalTrialBannerFlag.enabled
  const shouldShowFirstTrialPopup = canShowFirstTrialPopup(userInfo)
  const isAiLearningAvailable = normalizeCountryCode(userInfo.countryCode) === 'KR'
  const studentId = Number(userInfo.id)
  const shouldFetchAdditionalTrialEligibility =
    isAdditionalTrialBannerEnabled && Boolean(bearerToken) && Number.isFinite(studentId)

  const { data: purchaseableTrialTickets } = useQuery({
    ...subscribesEntityQueries.getTrialSubscribesByStudentId({ studentId, bearerToken }),
    enabled: shouldFetchAdditionalTrialEligibility,
  })

  const { data: enOnboardingState } = useQuery({
    queryKey: ['onboarding-state', bearerToken, 'en'],
    queryFn: async () => {
      if (!bearerToken) {
        return null
      }

      try {
        return await requestOnboardingState(bearerToken, 'en')
      } catch {
        return null
      }
    },
    enabled: shouldFetchAdditionalTrialEligibility,
    retry: false,
  })

  const { data: jpOnboardingState } = useQuery({
    queryKey: ['onboarding-state', bearerToken, 'ja'],
    queryFn: async () => {
      if (!bearerToken) {
        return null
      }

      try {
        return await requestOnboardingState(bearerToken, 'ja')
      } catch {
        return null
      }
    },
    enabled: shouldFetchAdditionalTrialEligibility,
    retry: false,
  })

  const purchaseableTrialLanguages = useMemo(
    () => getPurchaseableTrialLanguageTypes(purchaseableTrialTickets?.data),
    [purchaseableTrialTickets?.data],
  )
  const bookedOnboardingLanguages = useMemo(
    () => getBookedLanguageTrialTypes([enOnboardingState, jpOnboardingState]),
    [enOnboardingState, jpOnboardingState],
  )
  const resumableOnboardingLanguages = useMemo(
    () => getResumableAdditionalTrialLanguageTypes([enOnboardingState, jpOnboardingState]),
    [enOnboardingState, jpOnboardingState],
  )

  const handleEventClick = useCallback(
    (url: string) => () => {
      window.open(url, '_blank')
    },
    [],
  )

  const handleBannerClick = useCallback(() => {
    router.push('/ai-learning')
  }, [router])

  const handleEventBannerClick = useCallback(() => {
    window.open(PODOLINGO_EVENT_BANNER_URL, '_blank')
  }, [])

  const handleHexagonGmiClick = useCallback(() => {
    window.open(HEXAGON_GMI_BANNER_URL, '_blank')
  }, [])

  const handleWelcomeBackClick = useCallback(
    (url: string) => () => {
      const parsedUrl = new URL(url)
      parsedUrl.searchParams.set('external', 'true')
      window.open(parsedUrl.toString(), '_blank')
    },
    [],
  )

  useEffect(() => {
    if (!carouselApi) {
      return
    }

    setCarouselState(() => ({
      count: carouselApi.scrollSnapList().length,
      current: carouselApi.selectedScrollSnap() + 1,
    }))

    carouselApi.on('select', () => {
      setCarouselState((prev) => ({ ...prev, current: carouselApi.selectedScrollSnap() + 1 }))
    })
  }, [carouselApi])

  /**
   * 조건에 맞는 모든 배너를 수집
   */
  const banners = useMemo((): BannerItem[] => {
    return collectBanners({
      welcomeBackPromotion: welcomeBackPromotion ?? null,
      podolingoBannerFlag,
      isAdditionalTrialBannerEnabled,
      isAiLearningAvailable,
      canShowFirstTrialPopup: shouldShowFirstTrialPopup,
      trialClassCompYn,
      paymentYn,
      paymentInfo,
      trialPaymentInfo,
      trialClassCompInfo,
      purchaseableTrialLanguages,
      bookedOnboardingLanguages,
      resumableOnboardingLanguages,
      handlers: {
        handleBannerClick,
        handleEventBannerClick,
        handleEventClick,
        handleHexagonGmiClick,
        handleWelcomeBackClick,
      },
    })
  }, [
    welcomeBackPromotion,
    podolingoBannerFlag,
    isAdditionalTrialBannerEnabled,
    isAiLearningAvailable,
    shouldShowFirstTrialPopup,
    trialClassCompYn,
    paymentYn,
    paymentInfo,
    trialPaymentInfo,
    trialClassCompInfo,
    purchaseableTrialLanguages,
    bookedOnboardingLanguages,
    resumableOnboardingLanguages,
    handleEventClick,
    handleBannerClick,
    handleEventBannerClick,
    handleHexagonGmiClick,
    handleWelcomeBackClick,
  ])

  const indicatorState = useMemo(
    () => getBannerIndicatorState({ bannerCount: banners.length, carouselState }),
    [banners.length, carouselState],
  )

  useEffect(() => {
    if (!carouselApi) {
      return
    }

    carouselApi.reInit()
    setCarouselState({
      count: carouselApi.scrollSnapList().length,
      current: carouselApi.selectedScrollSnap() + 1,
    })
  }, [carouselApi, banners.length])

  if (banners.length === 1) {
    return <div className={HOME_BANNER_CONTAINER_CLASS_NAME}>{banners[0].component}</div>
  }

  return (
    <Carousel
      className={`relative ${HOME_BANNER_CONTAINER_CLASS_NAME}`}
      setApi={setCarouselApi}
      opts={{ loop: true }}
      plugins={[Autoplay({ delay: 5000 })]}
      data-testid="list-banners"
    >
      <CarouselContent>
        {banners.map((banner) => (
          <CarouselItem key={banner.id} className="shrink-0 w-full pl-0">
            {banner.component}
          </CarouselItem>
        ))}
      </CarouselContent>

      <CarouselIndicator
        className="absolute bottom-2 right-2 z-10 inline-flex items-center justify-center"
        color="white"
        size="small"
        count={indicatorState.count}
        current={indicatorState.current}
      />
    </Carousel>
  )
}
